// This file is part of ecbyts/ECBT (Environmental & Occupational Core Byte Tools).
// Copyright (C) 2026 Calvin Stefan Iost
// Licensed under the GNU Affero General Public License v3.0 (AGPL-3.0-only).

/* ================================================================
   BAYES-001 — i18n autônomo pt/en/es (SPEC §7).

   Precedente do próprio repo: Q2Q_LAYMAN_LABELS_PT/EN vivem no
   módulo (core/uncertainty/observationSchema.ts) sem depender dos
   JSON centrais. Neste ciclo os locales centrais estão com dono
   vivo (outra sessão) — um arquivo por dono (RF-15.1). Migração
   p/ locales centrais fica registrada no ADR como pendência.
   ================================================================ */

export type BayesLang = 'pt' | 'en' | 'es';

const STRINGS: Record<string, Record<BayesLang, string>> = {
    'tab.title': { pt: 'Avaliação Bayesiana', en: 'Bayesian Assessment', es: 'Evaluación Bayesiana' },
    'tab.tooltip': {
        pt: 'Atualize crenças sobre o modelo conforme novos dados chegam',
        en: 'Update model beliefs as new data arrives',
        es: 'Actualice creencias del modelo conforme llegan datos nuevos',
    },
    'tab.help': {
        pt: 'Tour guiado da Avaliação Bayesiana',
        en: 'Bayesian Assessment guided tour',
        es: 'Tour guiado de Evaluación Bayesiana',
    },
    'source.label': { pt: 'Fonte de dados', en: 'Data source', es: 'Fuente de datos' },
    'source.loading': { pt: 'Carregando fontes…', en: 'Loading sources…', es: 'Cargando fuentes…' },
    'source.empty': { pt: 'Nenhuma fonte disponível', en: 'No source available', es: 'Ninguna fuente disponible' },
    'source.voxelChannel': { pt: 'Canais de voxel', en: 'Voxel channels', es: 'Canales de vóxel' },
    'source.elementsProperty': {
        pt: 'Propriedades de elementos',
        en: 'Element properties',
        es: 'Propiedades de elementos',
    },
    'source.uncertaintyObs': {
        pt: 'Observações de incerteza',
        en: 'Uncertainty observations',
        es: 'Observaciones de incertidumbre',
    },
    'source.ecbtScore': { pt: 'Saída do ECBT Score', en: 'ECBT Score output', es: 'Salida del ECBT Score' },
    'target.label': { pt: 'Alvo', en: 'Target', es: 'Objetivo' },
    'target.empty': {
        pt: 'Selecione uma fonte primeiro',
        en: 'Select a source first',
        es: 'Seleccione una fuente primero',
    },
    'family.label': { pt: 'Família', en: 'Family', es: 'Familia' },
    'family.beta': { pt: 'Fração (Beta-Binomial)', en: 'Fraction (Beta-Binomial)', es: 'Fracción (Beta-Binomial)' },
    'family.normal': { pt: 'Média (Normal-Normal)', en: 'Mean (Normal-Normal)', es: 'Media (Normal-Normal)' },
    'family.gamma': { pt: 'Contagem (Gamma-Poisson)', en: 'Count (Gamma-Poisson)', es: 'Conteo (Gamma-Poisson)' },
    'prior.label': { pt: 'Ponto de partida (prior)', en: 'Starting point (prior)', es: 'Punto de partida (prior)' },
    'prior.param1': { pt: 'Parâmetro 1', en: 'Parameter 1', es: 'Parámetro 1' },
    'prior.param2': { pt: 'Parâmetro 2', en: 'Parameter 2', es: 'Parámetro 2' },
    'prior.weak': { pt: 'Pouco informativo', en: 'Weakly informative', es: 'Poco informativo' },
    'prior.historical': { pt: 'Usar dados históricos', en: 'Use historical data', es: 'Usar datos históricos' },
    'threshold.label': {
        pt: 'Limite regulatório (opcional)',
        en: 'Regulatory threshold (optional)',
        es: 'Límite regulatorio (opcional)',
    },
    'update.label': { pt: 'Atualizar crença', en: 'Update belief', es: 'Actualizar creencia' },
    'update.busy': { pt: 'Calculando…', en: 'Calculating…', es: 'Calculando…' },
    'result.title': { pt: 'Resultado (posterior)', en: 'Result (posterior)', es: 'Resultado (posterior)' },
    'result.mean': { pt: 'Média', en: 'Mean', es: 'Media' },
    'result.ci95': {
        pt: 'Intervalo de credibilidade 95%',
        en: '95% credible interval',
        es: 'Intervalo de credibilidad 95%',
    },
    'result.exceedance': {
        pt: 'Probabilidade de exceder o limite',
        en: 'Probability of exceeding threshold',
        es: 'Probabilidad de exceder el límite',
    },
    'result.bf': { pt: 'Evidência (fator de Bayes)', en: 'Evidence (Bayes factor)', es: 'Evidencia (factor de Bayes)' },
    'result.bf.negligible': { pt: 'descartável', en: 'negligible', es: 'despreciable' },
    'result.bf.positive': { pt: 'positiva', en: 'positive', es: 'positiva' },
    'result.bf.strong': { pt: 'forte', en: 'strong', es: 'fuerte' },
    'result.bf.very_strong': { pt: 'muito forte', en: 'very strong', es: 'muy fuerte' },
    'result.nobs': { pt: 'observações usadas', en: 'observations used', es: 'observaciones usadas' },
    'error.BAYES_EMPTY_INPUT': {
        pt: 'Nenhuma observação foi extraída da fonte selecionada.',
        en: 'No observations were extracted from the selected source.',
        es: 'Ninguna observación fue extraída de la fuente seleccionada.',
    },
    'error.BAYES_CROMWELL': {
        pt: 'O ponto de partida não pode ter certeza absoluta (regra de Cromwell).',
        en: 'The starting point cannot hold absolute certainty (Cromwell’s rule).',
        es: 'El punto de partida no puede tener certeza absoluta (regla de Cromwell).',
    },
    'error.BAYES_KIND_MISMATCH': {
        pt: 'Tipo de dado incompatível com a família escolhida.',
        en: 'Data kind incompatible with the chosen family.',
        es: 'Tipo de dato incompatible con la familia elegida.',
    },
    'error.GENERIC': { pt: 'Falha no cálculo.', en: 'Calculation failed.', es: 'Falló el cálculo.' },
    'tour.step1.title': { pt: '1. Escolha a fonte', en: '1. Choose the source', es: '1. Elija la fuente' },
    'tour.step1.desc': {
        pt: 'Todo dado do modelo — elemento, canal de voxel, observação de incerteza ou score — vira evidência.',
        en: 'Any model data — element, voxel channel, uncertainty observation or score — becomes evidence.',
        es: 'Cualquier dato del modelo — elemento, canal de vóxel, observación de incertidumbre o puntaje — se vuelve evidencia.',
    },
    'tour.step2.title': { pt: '2. Escolha o alvo', en: '2. Choose the target', es: '2. Elija el objetivo' },
    'tour.step2.desc': {
        pt: 'O alvo é a grandeza sobre a qual você quer atualizar sua crença.',
        en: 'The target is the quantity whose belief you want to update.',
        es: 'El objetivo es la magnitud cuya creencia quiere actualizar.',
    },
    'tour.step3.title': {
        pt: '3. Defina o ponto de partida',
        en: '3. Set the starting point',
        es: '3. Defina el punto de partida',
    },
    'tour.step3.desc': {
        pt: 'O prior expressa o que você já sabia antes dos dados — use o botão pouco informativo se não souber.',
        en: 'The prior expresses what you knew before the data — use weakly informative if unsure.',
        es: 'El prior expresa lo que sabía antes de los datos — use poco informativo si no sabe.',
    },
    'tour.step4.title': { pt: '4. Atualize a crença', en: '4. Update the belief', es: '4. Actualice la creencia' },
    'tour.step4.desc': {
        pt: 'O resultado mostra a média, o intervalo de credibilidade 95% e a chance de exceder o limite.',
        en: 'The result shows the mean, the 95% credible interval and the chance of exceeding the threshold.',
        es: 'El resultado muestra la media, el intervalo de credibilidad 95% y la chance de exceder el límite.',
    },
    'tour.next': { pt: 'Próximo', en: 'Next', es: 'Siguiente' },
    'tour.prev': { pt: 'Anterior', en: 'Previous', es: 'Anterior' },
    'tour.finish': { pt: 'Concluir', en: 'Finish', es: 'Terminar' },
    'tour.skip': { pt: 'Pular tour', en: 'Skip tour', es: 'Saltar tour' },
};

const LANG_ALIASES: Record<string, BayesLang> = {
    pt: 'pt',
    'pt-br': 'pt',
    pt_br: 'pt',
    en: 'en',
    'en-us': 'en',
    en_us: 'en',
    es: 'es',
    'es-la': 'es',
    es_la: 'es',
    'es-es': 'es',
};

// RF-ARCH-06 (ECBYTS-ELEVATION-001#39): o core não lê localStorage.
// A UI (utils/ui/bayesHubTab.ts) injeta o provedor de idioma no load;
// headless sem provider cai em 'en' — mesmo fallback do catch antigo.
let _langProvider: (() => string) | null = null;

/** Instala o provedor do idioma bruto (ex.: leitura de ecbyts_lang). */
export function setBayesLangProvider(provider: (() => string) | null): void {
    _langProvider = provider;
}

/** Idioma atual — mesma chave de storage do engine i18n central (ecbyts_lang), via provider injetado. */
export function detectBayesLang(): BayesLang {
    try {
        const raw = (_langProvider ? _langProvider() : '').toLowerCase();
        return LANG_ALIASES[raw] || 'en';
    } catch {
        return 'en';
    }
}

export function bayesT(key: string, lang?: BayesLang): string {
    const l = lang || detectBayesLang();
    const entry = STRINGS[key];
    if (!entry) return key;
    return entry[l] || entry.en || key;
}

export function bayesTAll(): Record<string, Record<BayesLang, string>> {
    return STRINGS;
}
